import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Plus, Trash2, Upload } from "lucide-react";
import { Modal } from "../ui/Modal";
import {
  adminNumbersApi,
  type AdminNumberListItem,
  type ImportNumberBody,
  type Lookup,
  type ResolvedNumber,
} from "../../api/adminNumbersApi";
import { customersAdminApi, type AdminCustomerListItem } from "../../api/customersAdminApi";
import { managersAdminApi, type ManagerAdmin } from "../../api/managersAdminApi";
import { formatPhoneNumber, parseNumberImportInput } from "../../lib/phone";
import { FAR_FUTURE, fromDateInput, today } from "../../lib/dates";
import { ScrollShadow } from "../ui/ScrollShadow";

type Step = "numbers" | "resolve" | "settings" | "customer" | "assignments" | "pricing" | "preview" | "running" | "done";

const stepTitle: Record<Step, string> = {
  numbers: "Import numbers · 1. Numbers",
  resolve: "Import numbers · 2. Country & description",
  settings: "Import numbers · 3. Number settings",
  customer: "Import numbers · 4. Customer",
  assignments: "Import numbers · 4. Managers & customers",
  pricing: "Import numbers · 5. Pricing",
  preview: "Import numbers · 6. Preview",
  running: "Importing numbers…",
  done: "Import finished",
};

const MAX_NUMBERS = 500;

/**
 * A resolved number plus the (editable) description that will be stored and,
 * for numbers that already exist, whether to overwrite them ("update") or
 * keep them out of the batch ("skip", which blocks Next until removed).
 */
type WizardRow = ResolvedNumber & {
  description: string;
  action: "create" | "update" | "skip";
  /** From the "manager, customer, number, provider" input format; empty in plain mode. */
  managerName: string;
  customerName: string;
  providerName: string;
};

const keyOf = (name: string) => name.trim().toLowerCase();
/** Distinct manager from the input, matched by name against the existing managers. */
type ManagerGroup = { key: string; name: string; numbers: number; matched: ManagerAdmin | null };
/** Distinct (manager, customer) pair from the input. */
type CustomerGroup = { key: string; managerKey: string; managerName: string; name: string; numbers: number };
/** Distinct provider name from the input, matched against the provider lookup. */
type ProviderGroup = { key: string; name: string; numbers: number; matched: Lookup | null };

type ResultRow =
  | { number: string; status: "ok"; dnisId: number; replaced: boolean; replacedDnisId: number | null }
  | { number: string; status: "error"; message: string };

/** DnisCPCPricingCustomer.CPC: null = any origin, "00" = fixed, "01" = mobile. */
const ORIGINS: { cpc: string | null; label: string }[] = [
  { cpc: null, label: "Any origin" },
  { cpc: "00", label: "Fixed origin" },
  { cpc: "01", label: "Mobile origin" },
];
const UNITS = ["MINUTE", "CALL"] as const;
const originLabel = (cpc: string | null) => ORIGINS.find((o) => o.cpc === cpc)?.label ?? cpc ?? "Any origin";

/** One DnisCPCPricingCustomer row to create; amounts are kept as text until submit. */
type TrafficRow = { id: number; cpc: string | null; unit: string; rate: string; description: string; eom: string };
let trafficRowSeq = 1;
const newTrafficRow = (init?: Partial<TrafficRow>): TrafficRow => ({
  id: trafficRowSeq++,
  cpc: null,
  unit: "MINUTE",
  rate: "",
  description: "Traffic cost",
  eom: "",
  ...init,
});

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "8px 10px",
  border: "2px solid var(--portal-border)",
  borderRadius: 8,
  fontSize: "0.9rem",
  fontFamily: "inherit",
  background: "white",
};

const cellInput: React.CSSProperties = { ...inputStyle, padding: "6px 8px", fontSize: "0.85rem" };

const ERROR_TEXT: Record<string, string> = {
  number_exists: "Number already exists",
  existing_not_found: "Existing number no longer found",
  existing_mismatch: "Existing number does not match",
  invalid_number: "Invalid number",
  provider_required: "Provider is required",
  number_type_required: "Number type is required",
  script_required: "Script is required",
  pricing_requires_customer: "Pricing requires a customer",
  invalid_cpc: "Invalid traffic origin",
  invalid_unit: "Invalid traffic unit",
  invalid_eom: "Invalid EOM days",
  invalid_amount: "Amounts cannot be negative",
  invalid_validity: "Invalid validity range",
};

/** Amount from the API → input text without float noise (0.05000000074 → "0.05"). */
function fmtAmount(n: number): string {
  return String(Number(n.toFixed(6)));
}

/** "0,15" / " 0.15 " → 0.15; "" → null; garbage → NaN. */
function toNum(s: string): number | null {
  const t = s.trim().replace(",", ".");
  if (!t) return null;
  return Number(t);
}

/**
 * Multi-step wizard that provisions one or more numbers:
 *  1. numbers  — paste numbers (one per line); normalised + de-duplicated
 *  2. resolve  — server derives country / area / description per number;
 *                unknown or already-existing numbers block the import
 *  3. settings — provider, number type, script, optional user tariffs
 *  4. customer — optional customer assignment
 *  5. pricing  — number (NRC/MRC), traffic (per minute) and termination
 *                pricing for that customer; optionally copied from an
 *                existing number of the same customer
 *  6. preview  — summary, then one import call per number (each is its
 *                own DB transaction; the batch continues on failure)
 */
export function NumberImportWizard({
  open,
  onClose,
  onImported,
}: {
  open: boolean;
  onClose: () => void;
  onImported: () => void;
}) {
  const [step, setStep] = useState<Step>("numbers");

  // lookups
  const [providers, setProviders] = useState<Lookup[]>([]);
  const [types, setTypes] = useState<Lookup[]>([]);
  const [scripts, setScripts] = useState<Lookup[]>([]);
  const [customers, setCustomers] = useState<AdminCustomerListItem[]>([]);
  const [managers, setManagers] = useState<ManagerAdmin[]>([]);
  const [pricelists, setPricelists] = useState<Lookup[]>([]);
  const [lookupsError, setLookupsError] = useState<string | null>(null);

  // step 1 + 2
  const [rawInput, setRawInput] = useState("");
  const [rows, setRows] = useState<WizardRow[]>([]);
  /** Input had "manager, customer, number" lines: customers come from the list, not from step 4. */
  const [assignmentMode, setAssignmentMode] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState<string | null>(null);

  // step 3
  const [providerId, setProviderId] = useState(0);
  const [numberTypeId, setNumberTypeId] = useState(0);
  const [scriptId, setScriptId] = useState(0);
  const [perMinute, setPerMinute] = useState("");
  const [perCall, setPerCall] = useState("");
  const [active, setActive] = useState(true);

  // step 4 (plain mode)
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [customerFilter, setCustomerFilter] = useState("");

  // step 4 (assignment mode): per manager key an existing managerId, or 0 to
  // create; per customer key an existing customerId, or 0 to create. Missing
  // entries fall back to the name match (see managerChoice / customerChoice).
  const [managerChoices, setManagerChoices] = useState<Record<string, number>>({});
  const [customerChoices, setCustomerChoices] = useState<Record<string, number>>({});
  /** Per provider key from the list: the providerId to use (0 = still to choose). */
  const [providerChoices, setProviderChoices] = useState<Record<string, number>>({});
  const [createError, setCreateError] = useState<string | null>(null);

  // step 5
  const [customerNumbers, setCustomerNumbers] = useState<AdminNumberListItem[]>([]);
  const [copyFromDnisId, setCopyFromDnisId] = useState(0);
  const [copying, setCopying] = useState(false);
  const [copyError, setCopyError] = useState<string | null>(null);
  const [setupFee, setSetupFee] = useState("");
  const [monthlyFee, setMonthlyFee] = useState("");
  const [numberDescription, setNumberDescription] = useState("Numbercost");
  const [trafficRows, setTrafficRows] = useState<TrafficRow[]>([]);
  const [terminationPricelistId, setTerminationPricelistId] = useState(0);

  // run
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [results, setResults] = useState<ResultRow[]>([]);

  const reset = () => {
    setStep("numbers");
    setRawInput("");
    setRows([]);
    setAssignmentMode(false);
    setResolving(false);
    setResolveError(null);
    setProviderId(0);
    setNumberTypeId(0);
    setScriptId(0);
    setPerMinute("");
    setPerCall("");
    setActive(true);
    setCustomerId(null);
    setCustomerFilter("");
    setManagerChoices({});
    setCustomerChoices({});
    setProviderChoices({});
    setCreateError(null);
    setCustomerNumbers([]);
    setCopyFromDnisId(0);
    setCopying(false);
    setCopyError(null);
    setSetupFee("");
    setMonthlyFee("");
    setNumberDescription("Numbercost");
    setTrafficRows([]);
    setTerminationPricelistId(0);
    setProgress({ done: 0, total: 0 });
    setResults([]);
  };

  // Fresh state + lookups every time the wizard opens.
  useEffect(() => {
    if (!open) return;
    reset();
    let cancelled = false;
    setLookupsError(null);
    Promise.all([
      adminNumbersApi.providers(),
      adminNumbersApi.numberTypes(),
      adminNumbersApi.scripts(),
      customersAdminApi.list(),
      adminNumbersApi.terminationPricelists(),
      managersAdminApi.list(),
    ])
      .then(([p, t, s, c, tp, m]) => {
        if (cancelled) return;
        setProviders(p);
        setTypes(t);
        setScripts(s);
        setCustomers(c.items);
        setPricelists(tp);
        setManagers(m);
      })
      .catch((err) => {
        if (!cancelled) setLookupsError(err instanceof Error ? err.message : "Failed to load lookups");
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const close = () => {
    if (step === "running") return;
    onClose();
  };
  const finish = () => {
    onClose();
    onImported();
  };

  // ---- derived -------------------------------------------------------------

  const parsed = useMemo(() => parseNumberImportInput(rawInput), [rawInput]);
  const isBlocked = (r: WizardRow) => !r.valid || (r.exists && r.action !== "update");
  const blockedRows = rows.filter(isBlocked).length;
  const updateRows = rows.filter((r) => r.action === "update").length;
  const canLeaveResolve = rows.length > 0 && blockedRows === 0 && rows.every((r) => r.description.trim().length > 0);

  const customer = customerId == null ? null : customers.find((c) => c.customerId === customerId) ?? null;
  const filteredCustomers = useMemo(() => {
    const q = customerFilter.trim().toLowerCase();
    return customers
      .filter((c) => !q || c.customerName.toLowerCase().includes(q))
      .sort((a, b) => Number(b.enabled) - Number(a.enabled) || a.customerName.localeCompare(b.customerName));
  }, [customers, customerFilter]);

  // ---- assignment mode: distinct managers / customers and their choices ----

  const managerGroups = useMemo<ManagerGroup[]>(() => {
    const map = new Map<string, ManagerGroup>();
    for (const r of rows) {
      if (!r.managerName) continue;
      const key = keyOf(r.managerName);
      const g = map.get(key);
      if (g) g.numbers += 1;
      else map.set(key, { key, name: r.managerName.trim(), numbers: 1, matched: managers.find((m) => keyOf(m.name) === key) ?? null });
    }
    return [...map.values()];
  }, [rows, managers]);

  const customerGroups = useMemo<CustomerGroup[]>(() => {
    const map = new Map<string, CustomerGroup>();
    for (const r of rows) {
      if (!r.customerName) continue;
      const managerKey = keyOf(r.managerName);
      const key = `${managerKey}|${keyOf(r.customerName)}`;
      const g = map.get(key);
      if (g) g.numbers += 1;
      else map.set(key, { key, managerKey, managerName: r.managerName.trim(), name: r.customerName.trim(), numbers: 1 });
    }
    return [...map.values()];
  }, [rows]);

  const providerGroups = useMemo<ProviderGroup[]>(() => {
    const map = new Map<string, ProviderGroup>();
    for (const r of rows) {
      if (!r.providerName) continue;
      const key = keyOf(r.providerName);
      const g = map.get(key);
      if (g) g.numbers += 1;
      else map.set(key, { key, name: r.providerName.trim(), numbers: 1, matched: providers.find((p) => keyOf(p.name) === key) ?? null });
    }
    return [...map.values()];
  }, [rows, providers]);
  /** providerId chosen for a provider group (0 = unresolved). */
  const providerChoice = (g: ProviderGroup): number => providerChoices[g.key] ?? g.matched?.id ?? 0;
  const unresolvedProviders = providerGroups.filter((g) => providerChoice(g) <= 0);
  /** Plain mode always needs the provider from step 3; assignment mode only for lines without a provider column. */
  const needsDefaultProvider = !assignmentMode || rows.some((r) => !r.providerName);
  const providerIdForRow = (r: WizardRow): number => {
    if (!r.providerName) return providerId;
    const g = providerGroups.find((pg) => pg.key === keyOf(r.providerName));
    return g ? providerChoice(g) : providerId;
  };

  /** Existing managerId for a manager group, or 0 when it will be created. */
  const managerChoice = (g: ManagerGroup): number => managerChoices[g.key] ?? g.matched?.managerId ?? 0;
  const managerIdForKey = (managerKey: string): number => {
    const g = managerGroups.find((m) => m.key === managerKey);
    return g ? managerChoice(g) : 0;
  };
  /** Customer under the chosen manager with the same name, or null. */
  const matchCustomer = (g: CustomerGroup): AdminCustomerListItem | null => {
    const managerId = managerIdForKey(g.managerKey);
    if (managerId <= 0) return null;
    return customers.find((c) => c.managerId === managerId && keyOf(c.customerName) === keyOf(g.name)) ?? null;
  };
  /** Existing customerId for a customer group, or 0 when it will be created. */
  const customerChoice = (g: CustomerGroup): number => customerChoices[g.key] ?? matchCustomer(g)?.customerId ?? 0;
  const managersToCreate = managerGroups.filter((g) => managerChoice(g) <= 0);
  const customersToCreate = customerGroups.filter((g) => customerChoice(g) <= 0);
  const customerLabel = (id: number) => customers.find((c) => c.customerId === id)?.customerName ?? `customer ${id}`;
  const managerLabel = (id: number) => managers.find((m) => m.managerId === id)?.name ?? `manager ${id}`;

  const pricingErrors = useMemo(() => {
    const errs: string[] = [];
    const check = (label: string, v: string) => {
      const n = toNum(v);
      if (n !== null && (Number.isNaN(n) || n < 0)) errs.push(`${label}: "${v}" is not a valid amount`);
    };
    check("Setup fee", setupFee);
    check("Monthly fee", monthlyFee);
    trafficRows.forEach((t, i) => {
      const r = toNum(t.rate);
      if (r === null || Number.isNaN(r) || r < 0) errs.push(`Traffic rate ${i + 1} (${originLabel(t.cpc)}): enter a valid amount or remove the row`);
      if (t.eom.trim() && !/^\d+$/.test(t.eom.trim())) errs.push(`Traffic rate ${i + 1}: EOM must be a whole number of days`);
    });
    return errs;
  }, [setupFee, monthlyFee, trafficRows]);

  const tariffErrors = useMemo(() => {
    const errs: string[] = [];
    for (const [label, v] of [["Tariff per minute", perMinute], ["Tariff per call", perCall]] as const) {
      if (v.trim() && !/^\d+$/.test(v.trim())) errs.push(`${label} must be a whole number`);
    }
    return errs;
  }, [perMinute, perCall]);

  const pricingSummary = useMemo(() => {
    if (!assignmentMode && customerId == null) return "No customer, no pricing";
    const parts: string[] = [];
    const nrc = toNum(setupFee);
    const mrc = toNum(monthlyFee);
    if (nrc !== null || mrc !== null) parts.push(`NRC ${(nrc ?? 0).toFixed(2)} · MRC ${(mrc ?? 0).toFixed(2)}`);
    trafficRows.forEach((t) => {
      const r = toNum(t.rate);
      if (r !== null && !Number.isNaN(r)) parts.push(`${originLabel(t.cpc)} ${r}/${t.unit === "CALL" ? "call" : "min"}`);
    });
    if (terminationPricelistId > 0) {
      parts.push(`Termination: ${pricelists.find((p) => p.id === terminationPricelistId)?.name ?? terminationPricelistId}`);
    }
    return parts.length ? parts.join(" · ") : "No pricing";
  }, [assignmentMode, customerId, setupFee, monthlyFee, trafficRows, terminationPricelistId, pricelists]);

  // ---- actions -------------------------------------------------------------

  async function resolve() {
    setResolving(true);
    setResolveError(null);
    try {
      const resolved = await adminNumbersApi.resolve(parsed.numbers);
      const byNumber = new Map(parsed.entries.map((e) => [e.number, e]));
      setRows(
        resolved.map((r) => ({
          ...r,
          description: r.description,
          action: r.exists ? "skip" : "create",
          managerName: byNumber.get(r.number)?.managerName ?? "",
          customerName: byNumber.get(r.number)?.customerName ?? "",
          providerName: byNumber.get(r.number)?.providerName ?? "",
        })),
      );
      setAssignmentMode(parsed.assignments);
      setManagerChoices({});
      setCustomerChoices({});
      setProviderChoices({});
      setStep("resolve");
    } catch (err) {
      setResolveError(err instanceof Error ? err.message : "Failed to resolve numbers");
    } finally {
      setResolving(false);
    }
  }

  const removeBlocked = () => setRows((rs) => rs.filter((r) => !isBlocked(r)));
  const setDescription = (number: string, description: string) =>
    setRows((rs) => rs.map((r) => (r.number === number ? { ...r, description } : r)));
  const setAction = (number: string, action: WizardRow["action"]) =>
    setRows((rs) => rs.map((r) => (r.number === number ? { ...r, action } : r)));

  async function goToPricing() {
    if (assignmentMode) {
      // Several customers: pricing is entered once and applied to all;
      // "copy from an existing number" needs one customer and is hidden.
      setStep("pricing");
      return;
    }
    if (customerId == null) {
      setStep("preview");
      return;
    }
    setStep("pricing");
    setCopyError(null);
    try {
      const page = await adminNumbersApi.list({ customerId, skip: 0, take: 500 });
      setCustomerNumbers(page.items);
    } catch (err) {
      setCopyError(err instanceof Error ? err.message : "Failed to load the customer's numbers");
    }
  }

  async function copyFrom(dnisId: number) {
    setCopyFromDnisId(dnisId);
    if (dnisId <= 0 || customerId == null) return;
    setCopying(true);
    setCopyError(null);
    try {
      const p = await adminNumbersApi.pricing(dnisId);
      const now = Date.now();
      const current = <T extends { customerId: number | null; validFromUtc: string; validTillUtc: string }>(items: T[]) =>
        items
          .filter((i) => i.customerId === customerId && new Date(i.validTillUtc).getTime() > now)
          .sort((a, b) => new Date(b.validFromUtc).getTime() - new Date(a.validFromUtc).getTime());

      const n = current(p.number)[0];
      setSetupFee(n ? fmtAmount(n.setupFee) : "");
      setMonthlyFee(n ? fmtAmount(n.monthlyFee) : "");
      if (n?.description) setNumberDescription(n.description);

      // Every current traffic row of the source number becomes a row here
      // (a number can have several per origin, e.g. traffic + platform cost).
      setTrafficRows(
        current(p.traffic).map((i) =>
          newTrafficRow({
            cpc: i.cpc ?? null,
            unit: (i.unit || "MINUTE").toUpperCase(),
            rate: fmtAmount(i.rate),
            description: i.description || "Traffic cost",
            eom: "",
          }),
        ),
      );

      const term = current(p.termination)[0];
      setTerminationPricelistId(term ? term.terminationPricelistId : 0);
    } catch (err) {
      setCopyError(err instanceof Error ? err.message : "Failed to load pricing");
    } finally {
      setCopying(false);
    }
  }

  function buildBody(row: WizardRow, rowCustomerId: number | null): ImportNumberBody {
    const nrc = toNum(setupFee);
    const mrc = toNum(monthlyFee);
    const traffic = trafficRows.map((t) => ({
      cpc: t.cpc,
      unit: t.unit,
      rate: toNum(t.rate) ?? 0,
      description: t.description.trim() || "Traffic cost",
      eom: t.eom.trim() ? Number(t.eom) : null,
    }));
    return {
      number: row.number,
      description: row.description.trim(),
      providerId: providerIdForRow(row),
      numberTypeId,
      scriptId,
      userTariffPerMinute: perMinute.trim() ? Number(perMinute) : null,
      userTariffPerCall: perCall.trim() ? Number(perCall) : null,
      validFromUtc: fromDateInput(today(), today()),
      validTillUtc: fromDateInput(FAR_FUTURE, FAR_FUTURE),
      active,
      customerId: rowCustomerId,
      existingDnisId: row.action === "update" ? row.existingDnisId : null,
      pricing:
        rowCustomerId == null
          ? null
          : {
              number:
                nrc === null && mrc === null
                  ? null
                  : { setupFee: nrc ?? 0, monthlyFee: mrc ?? 0, description: numberDescription.trim() || "Numbercost" },
              traffic,
              terminationPricelistId: terminationPricelistId > 0 ? terminationPricelistId : null,
            },
    };
  }

  async function run() {
    setCreateError(null);
    setStep("running");
    setResults([]);
    setProgress({ done: 0, total: rows.length });

    // Assignment mode: create the missing managers and customers first, once
    // each, so every number below gets a real customerId. A failure here
    // stops the import before any number is touched.
    const customerIdByKey = new Map<string, number>();
    if (assignmentMode) {
      try {
        const managerIdByKey = new Map<string, number>();
        for (const g of managerGroups) {
          const chosen = managerChoice(g);
          if (chosen > 0) {
            managerIdByKey.set(g.key, chosen);
          } else {
            const created = await managersAdminApi.create(g.name);
            managerIdByKey.set(g.key, created.managerId);
          }
        }
        for (const g of customerGroups) {
          const chosen = customerChoice(g);
          if (chosen > 0) {
            customerIdByKey.set(g.key, chosen);
          } else {
            const managerId = managerIdByKey.get(g.managerKey) ?? 0;
            if (managerId <= 0) throw new Error(`No manager for customer "${g.name}"`);
            const created = await customersAdminApi.create(g.name, managerId);
            customerIdByKey.set(g.key, created.customerId);
          }
        }
      } catch (err) {
        setCreateError(`Creating managers/customers failed, nothing was imported: ${err instanceof Error ? err.message : "unknown error"}`);
        setStep("preview");
        return;
      }
    }

    const out: ResultRow[] = [];
    for (const row of rows) {
      try {
        const rowCustomerId = assignmentMode
          ? customerIdByKey.get(`${keyOf(row.managerName)}|${keyOf(row.customerName)}`) ?? null
          : customerId;
        const res = await adminNumbersApi.importOne(buildBody(row, rowCustomerId));
        out.push({ number: row.number, status: "ok", dnisId: res.dnisId, replaced: res.replaced, replacedDnisId: res.replacedDnisId });
      } catch (err) {
        const raw = err instanceof Error ? err.message : "Import failed";
        out.push({ number: row.number, status: "error", message: ERROR_TEXT[raw] ?? raw });
      }
      setProgress({ done: out.length, total: rows.length });
    }
    setResults(out);
    setStep("done");
  }

  // ---- render ---------------------------------------------------------------

  return (
    <Modal open={open} onClose={close} title={stepTitle[step]} busy={step === "running" || resolving} maxWidth={980} footer={renderFooter()}>
      {lookupsError && <Alert>{lookupsError}</Alert>}
      {step === "numbers" && renderNumbers()}
      {step === "resolve" && renderResolve()}
      {step === "settings" && renderSettings()}
      {step === "customer" && renderCustomer()}
      {step === "assignments" && renderAssignments()}
      {step === "pricing" && renderPricing()}
      {step === "preview" && renderPreview()}
      {step === "running" && renderRunning()}
      {step === "done" && renderDone()}
    </Modal>
  );

  // ---- footer -------------------------------------------------------------

  function renderFooter() {
    const back = (to: Step) => (
      <button type="button" className="btn btn-secondary" onClick={() => setStep(to)}>
        Back
      </button>
    );
    const cancel = (
      <button type="button" className="btn btn-secondary" onClick={close}>
        Cancel
      </button>
    );
    switch (step) {
      case "numbers":
        return (
          <>
            {cancel}
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => void resolve()}
              disabled={resolving || parsed.mixed || parsed.numbers.length === 0 || parsed.numbers.length > MAX_NUMBERS}
            >
              {resolving ? "Resolving…" : "Next"}
            </button>
          </>
        );
      case "resolve":
        return (
          <>
            {back("numbers")}
            <button type="button" className="btn btn-primary" onClick={() => setStep("settings")} disabled={!canLeaveResolve}>
              Next
            </button>
          </>
        );
      case "settings":
        return (
          <>
            {back("resolve")}
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setStep(assignmentMode ? "assignments" : "customer")}
              disabled={(needsDefaultProvider && providerId <= 0) || numberTypeId <= 0 || scriptId <= 0 || tariffErrors.length > 0}
            >
              Next
            </button>
          </>
        );
      case "customer":
      case "assignments":
        return (
          <>
            {back("settings")}
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => void goToPricing()}
              disabled={step === "assignments" && unresolvedProviders.length > 0}
              title={step === "assignments" && unresolvedProviders.length > 0 ? "Choose a provider for every provider in the list" : undefined}
            >
              Next
            </button>
          </>
        );
      case "pricing":
        return (
          <>
            {back(assignmentMode ? "assignments" : "customer")}
            <button type="button" className="btn btn-primary" onClick={() => setStep("preview")} disabled={copying || pricingErrors.length > 0}>
              Next
            </button>
          </>
        );
      case "preview":
        return (
          <>
            {back(assignmentMode || customerId != null ? "pricing" : "customer")}
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => void run()}
              disabled={rows.length === 0}
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              <Upload size={16} /> Import {rows.length} {rows.length === 1 ? "number" : "numbers"}
            </button>
          </>
        );
      case "running":
        return null;
      case "done":
        return (
          <button type="button" className="btn btn-primary" onClick={finish}>
            Close
          </button>
        );
    }
  }

  // ---- steps --------------------------------------------------------------

  function renderNumbers() {
    const count = parsed.numbers.length;
    return (
      <div style={{ display: "grid", gap: 10 }}>
        <p style={{ margin: 0, color: "var(--portal-muted)", fontSize: "0.9rem" }}>
          Paste the numbers to import, one per line (comma or semicolon separated also works). Use the
          international format, e.g. <code>31101234567</code>; spaces, dashes and a leading "+" or "00" are ignored.
        </p>
        <p style={{ margin: 0, color: "var(--portal-muted)", fontSize: "0.9rem" }}>
          To import for several customers at once, use one line per number as <code>manager name, customer name, number, provider</code>
          {" "}(comma, semicolon or tab separated; the provider column is optional). Managers, customers and providers are matched by
          name; missing managers and customers can be created in step 4, and the customer choice is skipped. The two formats cannot be mixed.
        </p>
        <textarea
          className="portal-input-inline"
          style={{ ...inputStyle, minHeight: 220, resize: "vertical", fontFamily: "inherit" }}
          value={rawInput}
          onChange={(e) => setRawInput(e.target.value)}
          placeholder={"31101234567\n31201234567\n\nor\n\nFonix, Chester Travel, 441234567890, BT"}
          autoFocus
        />
        <div style={{ fontSize: "0.85rem", color: "var(--portal-muted)" }}>
          <strong style={{ color: "inherit" }}>{count}</strong> {count === 1 ? "number" : "numbers"}
          {parsed.assignments && <> · manager/customer per line</>}
          {parsed.duplicates > 0 && <> · {parsed.duplicates} duplicate{parsed.duplicates === 1 ? "" : "s"} removed</>}
          {parsed.invalid.length > 0 && (
            <> · <span style={{ color: "#c0392b" }}>{parsed.invalid.length} unparsable: {parsed.invalid.slice(0, 5).join(", ")}{parsed.invalid.length > 5 ? "…" : ""}</span></>
          )}
        </div>
        {count > MAX_NUMBERS && <Alert>At most {MAX_NUMBERS} numbers can be imported at once.</Alert>}
        {parsed.mixed && <Alert>Plain numbers and "manager, customer, number" lines cannot be mixed. Use one format for the whole list.</Alert>}
        {resolveError && <Alert>{resolveError}</Alert>}
      </div>
    );
  }

  function renderResolve() {
    return (
      <div style={{ display: "grid", gap: 10 }}>
        <p style={{ margin: 0, color: "var(--portal-muted)", fontSize: "0.9rem" }}>
          Country and area were derived from each number's prefix. Adjust the description where needed. Numbers
          without a known country must be removed; numbers that already exist can either be removed or be
          replaced (the current number record is ended now and a new one is created with the settings,
          customer and pricing from this wizard).
        </p>
        {blockedRows > 0 && (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
            <Alert>
              {blockedRows} {blockedRows === 1 ? "number is" : "numbers are"} blocked (unknown country, or already exists without "Replace").
            </Alert>
            <button type="button" className="btn btn-secondary" onClick={removeBlocked} style={{ whiteSpace: "nowrap" }}>
              Remove blocked rows
            </button>
          </div>
        )}
        <ScrollShadow style={{ border: "1px solid var(--portal-border)", borderRadius: 8 }} scrollStyle={{ maxHeight: 420, overflowY: "auto" }}>
          <table style={{ borderCollapse: "collapse", width: "100%", fontSize: "0.85rem" }}>
            <thead>
              <tr>
                <th style={thStyle}>#</th>
                <th style={thStyle}>Number</th>
                <th style={thStyle}>Country</th>
                <th style={thStyle}>Area</th>
                <th style={{ ...thStyle, minWidth: 260 }}>Description</th>
                <th style={thStyle}>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.number} style={isBlocked(r) ? { background: "#fdecea" } : r.action === "update" ? { background: "#fff8e6" } : undefined}>
                  <td style={tdStyle}>{i + 1}</td>
                  <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>{formatPhoneNumber(r.number, r.countryCode, r.areaCode)}</td>
                  <td style={tdStyle}>{r.countryName || "—"}</td>
                  <td style={tdStyle}>{r.areaName || "—"}</td>
                  <td style={{ ...tdStyle, maxWidth: "none" }}>
                    <input
                      className="portal-input-inline"
                      style={{ ...inputStyle, padding: "6px 8px", fontSize: "0.85rem" }}
                      value={r.description}
                      onChange={(e) => setDescription(r.number, e.target.value)}
                      disabled={isBlocked(r)}
                    />
                  </td>
                  <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                    {!r.valid ? (
                      <Chip tone="danger">Unknown country</Chip>
                    ) : r.exists ? (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                        <Chip tone="warn">Already exists</Chip>
                        <select
                          className="portal-input-inline"
                          style={{ ...cellInput, width: "auto" }}
                          value={r.action}
                          onChange={(e) => setAction(r.number, e.target.value as WizardRow["action"])}
                          aria-label="Action for existing number"
                        >
                          <option value="skip">Remove from batch</option>
                          <option value="update">Replace existing</option>
                        </select>
                      </span>
                    ) : (
                      <Chip tone="ok">OK</Chip>
                    )}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td style={tdStyle} colSpan={6}>No numbers left. Go back to add numbers.</td>
                </tr>
              )}
            </tbody>
          </table>
        </ScrollShadow>
      </div>
    );
  }

  function renderSettings() {
    return (
      <div style={{ display: "grid", gap: 12 }}>
        {assignmentMode && !needsDefaultProvider && (
          <div style={{ fontSize: "0.85rem", color: "var(--portal-muted)" }}>
            The provider comes from the list (per number); you match the names in the next step.
          </div>
        )}
        <div style={fieldGrid}>
          {needsDefaultProvider && (
            <Field label={assignmentMode ? "Provider (for lines without one)" : "Provider"}>
              <select className="portal-input-inline" style={inputStyle} value={providerId} onChange={(e) => setProviderId(Number(e.target.value))}>
                <option value={0}>Select provider…</option>
                {providers.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </Field>
          )}
          <Field label="Number type">
            <select className="portal-input-inline" style={inputStyle} value={numberTypeId} onChange={(e) => setNumberTypeId(Number(e.target.value))}>
              <option value={0}>Select type…</option>
              {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </Field>
          <Field label="Script">
            <select className="portal-input-inline" style={inputStyle} value={scriptId} onChange={(e) => setScriptId(Number(e.target.value))}>
              <option value={0}>Select script…</option>
              {scripts.map((sc) => <option key={sc.id} value={sc.id}>{sc.name}</option>)}
            </select>
          </Field>
          <Field label="User tariff per minute (optional)">
            <input className="portal-input-inline" style={inputStyle} inputMode="numeric" value={perMinute} onChange={(e) => setPerMinute(e.target.value)} placeholder="e.g. 15" />
          </Field>
          <Field label="User tariff per call (optional)">
            <input className="portal-input-inline" style={inputStyle} inputMode="numeric" value={perCall} onChange={(e) => setPerCall(e.target.value)} placeholder="e.g. 0" />
          </Field>
        </div>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: "0.9rem", cursor: "pointer" }}>
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          Active
        </label>
        {tariffErrors.length > 0 && <Alert>{tariffErrors.join(" · ")}</Alert>}
        <div style={{ fontSize: "0.85rem", color: "var(--portal-muted)" }}>
          All numbers are created <strong style={{ color: "inherit" }}>{active ? "active" : "inactive"}</strong>, valid from the moment of import (UTC) until {FAR_FUTURE}.
          These settings apply to every number in this batch; individual numbers can be adjusted afterwards.
        </div>
      </div>
    );
  }

  function renderCustomer() {
    return (
      <div style={{ display: "grid", gap: 12 }}>
        <p style={{ margin: 0, color: "var(--portal-muted)", fontSize: "0.9rem" }}>
          Assign the numbers to a customer. Without a customer the numbers are created unassigned and no pricing is set.
        </p>
        <Field label="Filter customers">
          <input
            className="portal-input-inline"
            style={inputStyle}
            value={customerFilter}
            onChange={(e) => setCustomerFilter(e.target.value)}
            placeholder="Type to filter…"
            autoFocus
          />
        </Field>
        <Field label="Customer">
          <select
            className="portal-input-inline"
            style={inputStyle}
            value={customerId ?? 0}
            onChange={(e) => {
              const id = Number(e.target.value);
              setCustomerId(id > 0 ? id : null);
              // Pricing belongs to a customer: start over when it changes.
              setCopyFromDnisId(0);
            }}
            size={Math.min(12, Math.max(4, filteredCustomers.length + 1))}
          >
            <option value={0}>— No customer (do not assign) —</option>
            {filteredCustomers.map((c) => (
              <option key={c.customerId} value={c.customerId}>
                {c.customerName}{c.enabled ? "" : " (disabled)"} · {c.managerName}
              </option>
            ))}
          </select>
        </Field>
        <div style={{ fontSize: "0.85rem", color: "var(--portal-muted)" }}>
          Selected: <strong style={{ color: "inherit" }}>{customer ? customer.customerName : "none"}</strong>
        </div>
      </div>
    );
  }

  function renderAssignments() {
    const setManager = (g: ManagerGroup, id: number) => {
      setManagerChoices((c) => ({ ...c, [g.key]: id }));
      // Customer choices under this manager were made against another manager.
      setCustomerChoices((c) => {
        const next = { ...c };
        for (const cg of customerGroups) if (cg.managerKey === g.key) delete next[cg.key];
        return next;
      });
    };
    return (
      <div style={{ display: "grid", gap: 14 }}>
        <p style={{ margin: 0, color: "var(--portal-muted)", fontSize: "0.9rem" }}>
          Managers and customers from your list, matched by name. Choose an existing one where the name differs, or
          create it: missing managers and customers are created at the start of the import, before any number.
        </p>

        {providerGroups.length > 0 && (
          <Section title={`Providers (${providerGroups.length})`}>
            <ScrollShadow>
              <table style={{ borderCollapse: "collapse", width: "100%", fontSize: "0.85rem" }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Provider in list</th>
                    <th style={thStyle}>Numbers</th>
                    <th style={thStyle}>Status</th>
                    <th style={{ ...thStyle, minWidth: 260 }}>Use</th>
                  </tr>
                </thead>
                <tbody>
                  {providerGroups.map((g) => {
                    const chosen = providerChoice(g);
                    return (
                      <tr key={g.key} style={chosen <= 0 ? { background: "#fdecea" } : undefined}>
                        <td style={tdStyle}>{g.name}</td>
                        <td style={tdStyle}>{g.numbers}</td>
                        <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                          {chosen <= 0 ? <Chip tone="danger">Not found</Chip> : g.matched?.id === chosen ? <Chip tone="ok">Found</Chip> : <Chip tone="ok">Existing</Chip>}
                        </td>
                        <td style={{ ...tdStyle, maxWidth: "none" }}>
                          <select className="portal-input-inline" style={cellInput} value={chosen} onChange={(e) => setProviderChoices((c) => ({ ...c, [g.key]: Number(e.target.value) }))}>
                            <option value={0}>Select provider…</option>
                            {providers.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </ScrollShadow>
            <div style={{ fontSize: "0.85rem", color: "var(--portal-muted)" }}>Providers cannot be created here; pick the existing one each name stands for.</div>
          </Section>
        )}

        <Section title={`Managers (${managerGroups.length})`}>
          <ScrollShadow>
            <table style={{ borderCollapse: "collapse", width: "100%", fontSize: "0.85rem" }}>
              <thead>
                <tr>
                  <th style={thStyle}>Manager in list</th>
                  <th style={thStyle}>Numbers</th>
                  <th style={thStyle}>Status</th>
                  <th style={{ ...thStyle, minWidth: 260 }}>Use</th>
                </tr>
              </thead>
              <tbody>
                {managerGroups.map((g) => {
                  const chosen = managerChoice(g);
                  return (
                    <tr key={g.key}>
                      <td style={tdStyle}>{g.name}</td>
                      <td style={tdStyle}>{g.numbers}</td>
                      <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                        {chosen <= 0 ? <Chip tone="warn">Will be created</Chip> : g.matched?.managerId === chosen ? <Chip tone="ok">Found</Chip> : <Chip tone="ok">Existing</Chip>}
                      </td>
                      <td style={{ ...tdStyle, maxWidth: "none" }}>
                        <select className="portal-input-inline" style={cellInput} value={chosen} onChange={(e) => setManager(g, Number(e.target.value))}>
                          <option value={0}>Create new manager "{g.name}"</option>
                          {managers.map((m) => <option key={m.managerId} value={m.managerId}>{m.name}</option>)}
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </ScrollShadow>
        </Section>

        <Section title={`Customers (${customerGroups.length})`}>
          <ScrollShadow>
            <table style={{ borderCollapse: "collapse", width: "100%", fontSize: "0.85rem" }}>
              <thead>
                <tr>
                  <th style={thStyle}>Manager</th>
                  <th style={thStyle}>Customer in list</th>
                  <th style={thStyle}>Numbers</th>
                  <th style={thStyle}>Status</th>
                  <th style={{ ...thStyle, minWidth: 260 }}>Use</th>
                </tr>
              </thead>
              <tbody>
                {customerGroups.map((g) => {
                  const managerId = managerIdForKey(g.managerKey);
                  const chosen = customerChoice(g);
                  const matched = matchCustomer(g);
                  const options = customers
                    .filter((c) => managerId > 0 && c.managerId === managerId)
                    .sort((a, b) => a.customerName.localeCompare(b.customerName));
                  return (
                    <tr key={g.key}>
                      <td style={tdStyle}>{managerId > 0 ? managerLabel(managerId) : <>{g.managerName} <Chip tone="warn">new</Chip></>}</td>
                      <td style={tdStyle}>{g.name}</td>
                      <td style={tdStyle}>{g.numbers}</td>
                      <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                        {chosen <= 0 ? <Chip tone="warn">Will be created</Chip> : matched?.customerId === chosen ? <Chip tone="ok">Found</Chip> : <Chip tone="ok">Existing</Chip>}
                      </td>
                      <td style={{ ...tdStyle, maxWidth: "none" }}>
                        <select
                          className="portal-input-inline"
                          style={cellInput}
                          value={chosen}
                          onChange={(e) => setCustomerChoices((c) => ({ ...c, [g.key]: Number(e.target.value) }))}
                          disabled={managerId <= 0}
                          title={managerId <= 0 ? "The manager is new, so the customer is created under it" : undefined}
                        >
                          <option value={0}>Create new customer "{g.name}"{managerId > 0 ? ` under ${managerLabel(managerId)}` : ""}</option>
                          {options.map((c) => <option key={c.customerId} value={c.customerId}>{c.customerName}{c.enabled ? "" : " (disabled)"}</option>)}
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </ScrollShadow>
        </Section>

        <div style={{ fontSize: "0.85rem", color: "var(--portal-muted)" }}>
          {managersToCreate.length} {managersToCreate.length === 1 ? "manager" : "managers"} and {customersToCreate.length}{" "}
          {customersToCreate.length === 1 ? "customer" : "customers"} will be created.
        </div>
      </div>
    );
  }

  function renderPricing() {
    return (
      <div style={{ display: "grid", gap: 14 }}>
        <p style={{ margin: 0, color: "var(--portal-muted)", fontSize: "0.9rem" }}>
          {assignmentMode ? (
            <>Pricing for all {customerGroups.length} customers in the list. </>
          ) : (
            <>Pricing for <strong style={{ color: "inherit" }}>{customer?.customerName}</strong>. </>
          )}
          Every section is optional: leave the fields empty to skip it. The same pricing is applied to all numbers in this batch.
        </p>

        {!assignmentMode && <Section title="Copy from an existing number of this customer">
          <select
            className="portal-input-inline"
            style={inputStyle}
            value={copyFromDnisId}
            onChange={(e) => void copyFrom(Number(e.target.value))}
            disabled={copying}
          >
            <option value={0}>— Enter pricing manually —</option>
            {customerNumbers.map((n) => (
              <option key={n.dnisId} value={n.dnisId}>
                {n.dnisPresentation} · {n.description}
              </option>
            ))}
          </select>
          {copying && <div style={{ fontSize: "0.85rem", color: "var(--portal-muted)" }}>Loading pricing…</div>}
          {copyError && <Alert>{copyError}</Alert>}
        </Section>}

        <Section title="Number pricing (DnisPricingCustomer)">
          <div style={fieldGrid}>
            <Field label="Setup fee (NRC)">
              <input className="portal-input-inline" style={inputStyle} inputMode="decimal" value={setupFee} onChange={(e) => setSetupFee(e.target.value)} placeholder="e.g. 10.00" />
            </Field>
            <Field label="Monthly fee (MRC)">
              <input className="portal-input-inline" style={inputStyle} inputMode="decimal" value={monthlyFee} onChange={(e) => setMonthlyFee(e.target.value)} placeholder="e.g. 5.00" />
            </Field>
            <Field label="Description">
              <input className="portal-input-inline" style={inputStyle} value={numberDescription} onChange={(e) => setNumberDescription(e.target.value)} />
            </Field>
          </div>
        </Section>

        <Section title="Traffic pricing (DnisCPCPricingCustomer)">
          {trafficRows.length === 0 ? (
            <div style={{ fontSize: "0.85rem", color: "var(--portal-muted)" }}>No traffic rates. Add one row per origin / unit combination.</div>
          ) : (
            <ScrollShadow>
              <table style={{ borderCollapse: "collapse", width: "100%", fontSize: "0.85rem" }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Origin</th>
                    <th style={thStyle}>Unit</th>
                    <th style={thStyle}>Rate</th>
                    <th style={{ ...thStyle, minWidth: 200 }}>Description</th>
                    <th style={thStyle} title="Days after end of month">EOM</th>
                    <th style={thStyle} aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {trafficRows.map((t) => {
                    const update = (patch: Partial<TrafficRow>) =>
                      setTrafficRows((rows) => rows.map((r) => (r.id === t.id ? { ...r, ...patch } : r)));
                    return (
                      <tr key={t.id}>
                        <td style={tdStyle}>
                          <select className="portal-input-inline" style={cellInput} value={t.cpc ?? ""} onChange={(e) => update({ cpc: e.target.value || null })}>
                            {ORIGINS.map((o) => <option key={o.cpc ?? "any"} value={o.cpc ?? ""}>{o.label}</option>)}
                          </select>
                        </td>
                        <td style={tdStyle}>
                          <select className="portal-input-inline" style={cellInput} value={t.unit} onChange={(e) => update({ unit: e.target.value })}>
                            {UNITS.map((u) => <option key={u} value={u}>{u === "MINUTE" ? "Per minute" : "Per call"}</option>)}
                          </select>
                        </td>
                        <td style={tdStyle}>
                          <input className="portal-input-inline" style={{ ...cellInput, width: 110 }} inputMode="decimal" value={t.rate} onChange={(e) => update({ rate: e.target.value })} placeholder="e.g. 0.15" />
                        </td>
                        <td style={{ ...tdStyle, maxWidth: "none" }}>
                          <input className="portal-input-inline" style={cellInput} value={t.description} onChange={(e) => update({ description: e.target.value })} />
                        </td>
                        <td style={tdStyle}>
                          <input className="portal-input-inline" style={{ ...cellInput, width: 70 }} inputMode="numeric" value={t.eom} onChange={(e) => update({ eom: e.target.value })} placeholder="—" />
                        </td>
                        <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                          <button
                            type="button"
                            className="btn btn-secondary"
                            aria-label="Remove rate"
                            onClick={() => setTrafficRows((rows) => rows.filter((r) => r.id !== t.id))}
                            style={{ padding: "6px 8px", display: "inline-flex", alignItems: "center" }}
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </ScrollShadow>
          )}
          <div>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setTrafficRows((rows) => [...rows, newTrafficRow()])}
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              <Plus size={14} /> Add rate
            </button>
          </div>
        </Section>

        <Section title="Termination pricelist (Dnis_TerminationPricelist)">
          <select className="portal-input-inline" style={inputStyle} value={terminationPricelistId} onChange={(e) => setTerminationPricelistId(Number(e.target.value))}>
            <option value={0}>— No termination pricelist —</option>
            {pricelists.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </Section>

        {pricingErrors.length > 0 && <Alert>{pricingErrors.join(" · ")}</Alert>}
      </div>
    );
  }

  function renderPreview() {
    const name = (list: Lookup[], id: number) => list.find((l) => l.id === id)?.name ?? "—";
    return (
      <div style={{ display: "grid", gap: 12 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 8, fontSize: "0.9rem", padding: 12, border: "1px solid var(--portal-border)", borderRadius: 8, background: "var(--portal-surface)" }}>
          <Summary label="Provider" value={assignmentMode && !needsDefaultProvider ? `${providerGroups.length} from the list` : name(providers, providerId)} />
          <Summary label="Number type" value={name(types, numberTypeId)} />
          <Summary label="Script" value={name(scripts, scriptId)} />
          <Summary label="User tariffs" value={perMinute.trim() || perCall.trim() ? `${perMinute.trim() || "—"}/min · ${perCall.trim() || "—"}/call` : "none"} />
          <Summary label="Active" value={active ? "yes" : "no"} />
          <Summary label="Customer" value={assignmentMode ? `${customerGroups.length} customers from the list` : customer ? customer.customerName : "not assigned"} />
          <Summary label="Pricing" value={pricingSummary} />
        </div>
        {createError && <Alert>{createError}</Alert>}
        {assignmentMode && (managersToCreate.length > 0 || customersToCreate.length > 0) && (
          <Alert>
            {managersToCreate.length > 0 && <>New managers: {managersToCreate.map((g) => g.name).join(", ")}. </>}
            {customersToCreate.length > 0 && <>New customers: {customersToCreate.map((g) => `${g.name} (${g.managerName})`).join(", ")}. </>}
            They are created when you press Import, before the numbers.
          </Alert>
        )}
        {updateRows > 0 && (
          <Alert>
            {updateRows} existing {updateRows === 1 ? "number" : "numbers"} will be replaced: the current number record, its customer
            assignment and its pricing are all ended at the moment of import, and a new number record is created from that moment
            with the settings, customer and pricing from this wizard.
          </Alert>
        )}
        <ScrollShadow style={{ border: "1px solid var(--portal-border)", borderRadius: 8 }} scrollStyle={{ maxHeight: 360, overflowY: "auto" }}>
          <table style={{ borderCollapse: "collapse", width: "100%", fontSize: "0.85rem" }}>
            <thead>
              <tr>
                <th style={thStyle}>#</th>
                <th style={thStyle}>Number</th>
                <th style={thStyle}>Description</th>
                <th style={thStyle}>Country / area</th>
                {assignmentMode && <th style={thStyle}>Customer</th>}
                {assignmentMode && <th style={thStyle}>Provider</th>}
                <th style={thStyle}>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.number}>
                  <td style={tdStyle}>{i + 1}</td>
                  <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>{formatPhoneNumber(r.number, r.countryCode, r.areaCode)}</td>
                  <td style={tdStyle}>{r.description}</td>
                  <td style={tdStyle}>{r.countryName}{r.areaName ? ` / ${r.areaName}` : ""}</td>
                  {assignmentMode && <td style={tdStyle}>{previewCustomer(r)}</td>}
                  {assignmentMode && <td style={tdStyle}>{name(providers, providerIdForRow(r))}</td>}
                  <td style={tdStyle}>{r.action === "update" ? <Chip tone="warn">Replace</Chip> : <Chip tone="ok">Create</Chip>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollShadow>
      </div>
    );
  }

  /** "Customer · Manager" for a preview row, marking the ones that will be created. */
  function previewCustomer(r: WizardRow): React.ReactNode {
    const g = customerGroups.find((cg) => cg.key === `${keyOf(r.managerName)}|${keyOf(r.customerName)}`);
    if (!g) return "—";
    const chosen = customerChoice(g);
    const managerId = managerIdForKey(g.managerKey);
    const mgr = managerId > 0 ? managerLabel(managerId) : `${g.managerName} (new)`;
    return chosen > 0 ? `${customerLabel(chosen)} · ${mgr}` : `${g.name} (new) · ${mgr}`;
  }

  function renderRunning() {
    const pct = progress.total ? Math.round((progress.done / progress.total) * 100) : 0;
    return (
      <div style={{ display: "grid", gap: 10 }}>
        <div style={{ fontSize: "0.9rem" }}>
          {progress.done} of {progress.total} numbers…
        </div>
        <div style={{ height: 8, background: "var(--portal-surface)", borderRadius: 999, overflow: "hidden" }}>
          <div style={{ width: `${pct}%`, height: "100%", background: "var(--portal-primary)", transition: "width 0.15s" }} />
        </div>
      </div>
    );
  }

  function renderDone() {
    const ok = results.filter((r) => r.status === "ok").length;
    const failed = results.length - ok;
    return (
      <div style={{ display: "grid", gap: 10 }}>
        <div style={{ fontSize: "0.95rem" }}>
          <strong>{ok}</strong> imported · <strong style={failed ? { color: "#c0392b" } : undefined}>{failed}</strong> failed
        </div>
        <ScrollShadow style={{ border: "1px solid var(--portal-border)", borderRadius: 8 }} scrollStyle={{ maxHeight: 360, overflowY: "auto" }}>
          <table style={{ borderCollapse: "collapse", width: "100%", fontSize: "0.85rem" }}>
            <thead>
              <tr>
                <th style={thStyle}>Number</th>
                <th style={thStyle}>Result</th>
              </tr>
            </thead>
            <tbody>
              {results.map((r) => (
                <tr key={r.number} style={r.status === "error" ? { background: "#fdecea" } : undefined}>
                  <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>{r.number}</td>
                  <td style={tdStyle}>
                    {r.status === "ok" ? (
                      <Link to={`/admin/numbers/${r.dnisId}`}>
                        {r.replaced ? `Replaced dnisId ${r.replacedDnisId} → new dnisId ${r.dnisId}` : `Created (dnisId ${r.dnisId})`}
                      </Link>
                    ) : (
                      <span style={{ color: "#c0392b" }}>{r.message}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollShadow>
      </div>
    );
  }
}

// ---- small parts ---------------------------------------------------------

const fieldGrid: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: 12,
};

const thStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "8px 10px",
  borderBottom: "1px solid var(--portal-border)",
  color: "var(--portal-muted)",
  fontSize: "0.75rem",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  position: "sticky",
  top: 0,
  background: "white",
  whiteSpace: "nowrap",
};
const tdStyle: React.CSSProperties = {
  padding: "6px 10px",
  borderBottom: "1px solid var(--portal-border)",
  verticalAlign: "top",
  maxWidth: 240,
};

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "grid", gap: 4, fontSize: "0.85rem" }}>
      <span style={{ color: "var(--portal-muted)" }}>{label}</span>
      {children}
    </label>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "grid", gap: 8, padding: 12, border: "1px solid var(--portal-border)", borderRadius: 8 }}>
      <div style={{ fontWeight: 600, fontSize: "0.9rem" }}>{title}</div>
      {children}
    </div>
  );
}

function Summary({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={{ color: "var(--portal-muted)", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</div>
      <div>{value}</div>
    </div>
  );
}

function Chip({ tone, children }: { tone: "ok" | "warn" | "danger"; children: React.ReactNode }) {
  const colors = {
    ok: { color: "#1e7e34", background: "#e6f4ea", border: "#a3d9b1" },
    warn: { color: "#8a5a00", background: "#fff4e0", border: "#f0c36d" },
    danger: { color: "#c0392b", background: "#fdecea", border: "#e74c3c" },
  }[tone];
  return (
    <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 999, fontSize: "0.75rem", fontWeight: 600, color: colors.color, background: colors.background, border: `1px solid ${colors.border}` }}>
      {children}
    </span>
  );
}

function Alert({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "flex-start", color: "#c0392b", background: "#fdecea", border: "1px solid #e74c3c", borderRadius: 8, padding: 10, fontSize: "0.9rem" }}>
      <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
      <div>{children}</div>
    </div>
  );
}
